import { Link } from "react-router-dom";
import { Mail, ArrowUpRight, MapPin, MessageCircle } from "lucide-react";
import logo from "@/assets/nivexa_icon_80.webp";
import bgFooter from "@/assets/bg-footer.jpg";

const TELEGRAM_LINK = "https://t.me/NivexaAI";

const Footer = () => {
  return (
    <footer className="relative border-t border-border/20 overflow-hidden">
      {/* Background image */}
      <img
        src={bgFooter}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none"
        loading="lazy"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/95 to-background/80 pointer-events-none" />

      <div className="container mx-auto px-4 py-16 relative">
        {/* Top section with brand */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-12 pb-10 border-b border-border/20">
          <div className="space-y-3 max-w-sm">
            <Link to="/" className="flex items-center gap-2.5">
              <img src={logo} alt="Nivexa FX Logo" width={64} height={64} className="h-14 w-14 object-contain" loading="lazy" />
              <span className="font-display font-bold text-lg tracking-wide text-foreground">NivexA <span className="text-primary">FX</span></span>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed">
              A global financial technology company focused on developing intelligent trading software and analytical tools.
            </p>
            {/* Dubai Location */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary shrink-0" />
              <span className="text-2xl leading-none">🇦🇪</span>
              <span>Dubai, United Arab Emirates</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="mailto:support@nivexafx.com"
              className="h-10 w-10 rounded-full border border-border/40 bg-secondary/30 flex items-center justify-center hover:border-primary/40 hover:bg-primary/10 transition-all"
              aria-label="Email"
            >
              <Mail className="h-4 w-4 text-muted-foreground" />
            </a>
            <a
              href={TELEGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 w-10 rounded-full border border-border/40 bg-secondary/30 flex items-center justify-center hover:border-primary/40 hover:bg-primary/10 transition-all"
              aria-label="Telegram"
            >
              <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
            </a>
          </div>
        </div>

        {/* Links grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Product */}
          <div className="space-y-4">
            <h3 className="font-display font-semibold text-xs uppercase tracking-widest text-foreground/70">Product</h3>
            <div className="flex flex-col gap-2.5">
              {[
                { to: "/product", label: "RapidX EA" },
                { to: "/pricing", label: "Pricing" },
                { to: "/blog", label: "Education Hub" },
                { to: "/faq", label: "FAQ" },
              ].map((l) => (
                <Link key={l.to} to={l.to} className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group">
                  {l.label}
                  <ArrowUpRight className="h-3 w-3 opacity-0 -translate-y-0.5 group-hover:opacity-100 group-hover:translate-y-0 transition-all" />
                </Link>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div className="space-y-4">
            <h3 className="font-display font-semibold text-xs uppercase tracking-widest text-foreground/70">Legal</h3>
            <div className="flex flex-col gap-2.5">
              {[
                { to: "/legal/risk-disclaimer", label: "Risk Disclaimer" },
                { to: "/legal/terms", label: "Terms & Conditions" },
                { to: "/legal/privacy", label: "Privacy Policy" },
                { to: "/legal/refund", label: "Refund Policy" },
                { to: "/legal/cookies", label: "Cookie Policy" },
              ].map((l) => (
                <Link key={l.to} to={l.to} className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group">
                  {l.label}
                  <ArrowUpRight className="h-3 w-3 opacity-0 -translate-y-0.5 group-hover:opacity-100 group-hover:translate-y-0 transition-all" />
                </Link>
              ))}
            </div>
          </div>

          {/* Support */}
          <div className="space-y-4">
            <h3 className="font-display font-semibold text-xs uppercase tracking-widest text-foreground/70">Support</h3>
            <div className="flex flex-col gap-2.5">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <MessageCircle className="h-3.5 w-3.5 text-primary" />
                Live Chat (via website)
              </span>
              <a href="mailto:support@nivexafx.com" className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-primary" />
                support@nivexafx.com
              </a>
              <a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5">
                <svg className="h-3.5 w-3.5 text-primary" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                Telegram Support
              </a>
            </div>
          </div>

          {/* Location & Stay Updated */}
          <div className="space-y-4">
            <h3 className="font-display font-semibold text-xs uppercase tracking-widest text-foreground/70">Global Presence</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="text-lg">🇦🇪</span>
              <span>Dubai, UAE</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Serving traders worldwide with cutting-edge trading automation technology from the heart of the Middle East's financial hub.
            </p>
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium">
              Education Hub <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border/20 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <p className="text-xs text-muted-foreground/60">
              © {new Date().getFullYear()} Nivexa FX. All rights reserved.
            </p>
            <span className="text-xs text-muted-foreground/40">•</span>
            <span className="text-xs text-muted-foreground/50 flex items-center gap-1">
              🇦🇪 Dubai, UAE
            </span>
          </div>
          <p className="text-xs text-muted-foreground/50 text-center max-w-xl leading-relaxed">
            Trading involves substantial risk. Past performance does not guarantee future results. This is not financial advice.
          </p>
        </div>
        <div className="text-center mt-4">
          <p className="text-xs text-muted-foreground/40">
            Website developed & managed by{" "}
            <a href="https://arionastudio.com/" target="_blank" rel="noopener noreferrer" className="text-primary/60 hover:text-primary transition-colors">
              Ariona Studio
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
